// src/js/shims/expose-globals.js
// -------------------------------------------------------------
// 1) Expose selected app modules to window for inline scripts
// -------------------------------------------------------------
import { ModelManager } from "../modules/ModelManager.js";
import * as characterData from "../modules/characterData.js";
import * as CharacterFieldOrganizer from "../modules/CharacterFieldOrganizer.js";

window.ModelManager = ModelManager;
window.characterData = characterData;
window.CharacterFieldOrganizer = CharacterFieldOrganizer;

// -------------------------------------------------------------
// 2) Initialize Firebase ONCE and expose to window
//    - Uses CDN modules via dynamic import (works with bundlers)
//    - Emits a "firebaseReady" event when initialized
// -------------------------------------------------------------
(async function initFirebaseOnce() {
  // If already initialized (e.g., hot navigation), just notify
  if (window.firebaseAuth && window.firebaseDB && window.firebaseModules) {
    try {
      window.dispatchEvent(
        new CustomEvent("firebaseReady", {
          detail: {
            auth: window.firebaseAuth,
            db: window.firebaseDB,
            modules: window.firebaseModules,
          },
        }),
      );
    } catch (_) {}
    return;
  }

  try {
    // Load modular Firebase CDN ESM at runtime (keeps bundle light)
    const appMod = await import(
      "https://www.gstatic.com/firebasejs/10.7.1/firebase-app.js"
    );
    const authMod = await import(
      "https://www.gstatic.com/firebasejs/10.7.1/firebase-auth.js"
    );
    const fsMod = await import(
      "https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js"
    );

    const { initializeApp, getApps, getApp } = appMod;
    const {
      getAuth,
      GoogleAuthProvider,
      signInWithPopup,
      signInWithEmailAndPassword,
      createUserWithEmailAndPassword,
      signInAnonymously,
      signOut,
      onAuthStateChanged,
      sendPasswordResetEmail,
      EmailAuthProvider,
      linkWithCredential,
      getAdditionalUserInfo,
    } = authMod;

    const {
      getFirestore,
      collection,
      addDoc,
      getDocs,
      query,
      where,
      orderBy,
      limit,
      Timestamp,
      deleteDoc,
      doc,
      getDoc,
      setDoc,
      runTransaction,
    } = fsMod;

    // --- Your Firebase project config (keep as-is / secure in env where possible)
    const firebaseConfig = {
      apiKey: "AIzaSyDKTdHQkWdOM2fEllU9aLEmniu69lRIqCg",
      authDomain: "legendlab-dc5ce.firebaseapp.com",
      projectId: "legendlab-dc5ce",
      storageBucket: "legendlab-dc5ce.firebasestorage.app",
      messagingSenderId: "119772776012",
      appId: "1:119772776012:web:b2c9d3041c6eaeeaee16e9",
      measurementId: "G-XWY8B7TQTZ",
    };

    // Idempotent app init
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

    // Create core services
    const auth = getAuth(app);
    const db = getFirestore(app);

    // Google provider (configure once)
    const googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({ prompt: "select_account" });

    // Expose to window
    window.firebaseApp = app;
    window.firebaseAuth = auth;
    window.firebaseDB = db;

    window.firebaseModules = {
      // Auth
      GoogleAuthProvider,
      googleProvider,
      signInWithPopup,
      signInWithEmailAndPassword,
      createUserWithEmailAndPassword,
      signInAnonymously,
      signOut,
      onAuthStateChanged,
      sendPasswordResetEmail,
      // Anonymous free-taste + account linking (preserve anon uid on upgrade)
      EmailAuthProvider,
      linkWithCredential,
      // New-account signal for conversion tracking (X Lead): consumers must
      // use getAdditionalUserInfo(result).isNewUser — result.additionalUserInfo
      // never resolves under the modular SDK. This shim WINS the
      // window.firebaseModules race, so it must expose everything pages use.
      getAdditionalUserInfo,
      // Firestore
      collection,
      addDoc,
      getDocs,
      query,
      where,
      orderBy,
      limit,
      Timestamp,
      deleteDoc,
      doc,
      getDoc,
      setDoc,
      runTransaction,
    };

    // Let pages know Firebase is ready
    try {
      window.dispatchEvent(
        new CustomEvent("firebaseReady", {
          detail: { auth, db, modules: window.firebaseModules },
        }),
      );
    } catch (_) {}
  } catch (err) {
    // In dev you’ll see this; in prod your build drops console.* per esbuild config
    console.error("[Firebase] init failed:", err);
  }
})();
